import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CssBaseline,
  Divider,
  Drawer,
  FormControlLabel,
  FormHelperText,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import Payment from "@mui/icons-material/Payment";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import { dashboardAppTheme } from "../../../theme/dashboardAppTheme";
import "../../styles/dashboard.css";
import "../../styles/agilysysPayOnboardingForm.css";

const AGILYSYS_PRODUCTS: { id: string; label: string }[] = [
  { id: "spa", label: "Spa" },
  { id: "visualOne", label: "Visual One" },
  { id: "igOnDemand", label: "IG OnDemand" },
  { id: "golf", label: "Golf" },
  { id: "versa", label: "Versa" },
  { id: "igFly", label: "IG Fly" },
  { id: "retail", label: "Retail" },
  { id: "stay", label: "Stay" },
  { id: "book", label: "Book" },
  { id: "salesCatering", label: "Sales & Catering" },
  { id: "lms", label: "LMS" },
  { id: "expressKiosk", label: "Express Kiosk" },
  { id: "reserveDining", label: "Reserve (Dining)" },
  { id: "infoGenesis", label: "InfoGenesis" },
  { id: "expressMobile", label: "Express Mobile" },
  { id: "reserveActivities", label: "Reserve (Activities)" },
  { id: "igKiosk", label: "IG Kiosk" },
  { id: "book4Time", label: "Book4Time" },
];

const PAY_SERVICES: { id: string; label: string }[] = [
  { id: "iframe", label: "iFrame" },
  { id: "hpp", label: "HPP" },
  { id: "pip", label: "PIP" },
  { id: "payGateway", label: "Agilysys Pay Gateway" },
  { id: "wallet", label: "Wallet" },
  { id: "giftCard", label: "Gift Card" },
];

const PAY_WALLETS: { id: string; label: string }[] = [
  { id: "applePay", label: "Apple Pay" },
  { id: "googlePay", label: "Google Pay" },
];

export type PayOnboardingEnvironment = "us_na" | "emea" | "apac";

const REGION_META: {
  id: PayOnboardingEnvironment;
  title: string;
  short: string;
}[] = [
  { id: "us_na", title: "US & North America", short: "US / NA" },
  { id: "emea", title: "EMEA", short: "EMEA" },
  { id: "apac", title: "APAC", short: "APAC" },
];

export interface AgilysysPayOnboardingFormValues {
  customerName: string;
  platformTenantId: string;
  products: string[];
  enterpriseId: string;
  enterpriseName: string;
  divisionId: string;
  divisionName: string;
  storeId: string;
  storeName: string;
  profitCenterOrOutletName: string;
  payServices: string[];
  wallets: string[];
  pmsDestinationUrl: string;
  enableCaptcha: boolean;
  paymentGateway: string;
  environmentRegion: PayOnboardingEnvironment;
}

const initialValues: AgilysysPayOnboardingFormValues = {
  customerName: "",
  platformTenantId: "",
  products: [],
  enterpriseId: "",
  enterpriseName: "",
  divisionId: "",
  divisionName: "",
  storeId: "",
  storeName: "",
  profitCenterOrOutletName: "",
  payServices: [],
  wallets: [],
  pmsDestinationUrl: "",
  enableCaptcha: true,
  paymentGateway: "",
  environmentRegion: "us_na",
};

function toggleInList(list: string[], id: string, checked: boolean): string[] {
  if (checked) {
    return list.includes(id) ? list : [...list, id];
  }
  return list.filter((x) => x !== id);
}

function completionPercent(v: AgilysysPayOnboardingFormValues): number {
  const checks = [
    v.customerName.trim().length > 0,
    v.platformTenantId.trim().length > 0,
    v.products.length > 0,
    [
      v.enterpriseId,
      v.enterpriseName,
      v.divisionId,
      v.divisionName,
      v.storeId,
      v.storeName,
    ].some((s) => s.trim().length > 0),
    v.profitCenterOrOutletName.trim().length > 0,
    v.payServices.length > 0,
    v.pmsDestinationUrl.trim().length > 0 || v.paymentGateway.trim().length > 0,
    Boolean(v.environmentRegion),
  ];
  const done = checks.filter(Boolean).length;
  return Math.round((done / checks.length) * 100);
}

function SectionCard(props: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  const { title, description, children } = props;
  return (
    <Paper
      variant="outlined"
      className="pay-onboarding-section-card"
      sx={{
        p: 2,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: description ? 0.5 : 1.5 }}>
        {title}
      </Typography>
      {description ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, lineHeight: 1.5 }}
        >
          {description}
        </Typography>
      ) : null}
      {children}
    </Paper>
  );
}

function SelectableChip(props: {
  label: string;
  selected: boolean;
  onToggle: () => void;
}) {
  const { label, selected, onToggle } = props;
  return (
    <Chip
      label={label}
      onClick={onToggle}
      color={selected ? "primary" : "default"}
      variant={selected ? "filled" : "outlined"}
      sx={{
        cursor: "pointer",
        fontWeight: selected ? 600 : 500,
        borderColor: selected ? "primary.main" : "divider",
        transition: "transform 120ms ease, box-shadow 120ms ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: 1,
        },
        "&:active": {
          transform: "translateY(0)",
        },
      }}
    />
  );
}

const AgilysysPayOnboardingFormInner: React.FC = () => {
  const [values, setValues] =
    useState<AgilysysPayOnboardingFormValues>(initialValues);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });

  const progress = useMemo(() => completionPercent(values), [values]);

  const environmentHelp = useMemo(
    () => ({
      us_na:
        "core.rguest.com, pay.rguest.com, pgc.rguest.com (traditional gift cards except SVS/Everi); api.rguest.com (Azure); agys-prod.azure-api.net/pay/svs/ (SVS); …/everi/ (Everi).",
      emea: "core.rguest.eu, pay.rguest.eu, pgc.rguest.eu; api.rguest.eu; same Azure SVS/Everi paths as US.",
      apac: "core.rguest.sg, pay.rguest.sg, pgc.rguest.sg; api.rguest.sg; same Azure SVS/Everi paths as US.",
    }),
    [],
  );

  const setField = <K extends keyof AgilysysPayOnboardingFormValues>(
    key: K,
    v: AgilysysPayOnboardingFormValues[K],
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSnack({
      open: true,
      message:
        "Thank you for your submission. We will review your request and get back to you soon.",
    });
  };

  const handleReset = () => {
    setValues(initialValues);
    setSnack({ open: true, message: "Form cleared." });
  };

  const sidebarInner = (
    <>
      <Stack
        className="form-two-col-row"
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          p: 1.5,
          borderRadius: 2,
          bgcolor: "#1f2937",
          border: "1px solid #374151",
        }}
      >
        <Payment sx={{ color: "#34d399", fontSize: 32 }} />
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ color: "#f9fafb", fontWeight: 700 }}
          >
            Agilysys Pay
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 0.5 }}
        >
          <Typography
            variant="caption"
            sx={{ color: "#9ca3af", fontWeight: 600 }}
          >
            Completion
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#10b981", fontWeight: 700 }}
          >
            {progress}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: "#374151",
            "& .MuiLinearProgress-bar": { borderRadius: 4, bgcolor: "#10b981" },
          }}
        />
      </Box>

      <Divider sx={{ borderColor: "#374151", my: 2 }} />

      <Typography
        variant="caption"
        sx={{ color: "#9ca3af", fontWeight: 600, display: "block", mb: 1 }}
      >
        Live summary
      </Typography>
      <Stack spacing={1} className="pay-onboarding-sidebar-stack">
        <Stack
          className="form-checkbox-row form-two-col-row"
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
        >
          <Chip
            size="small"
            label={`${values.products.length} products`}
            sx={{ bgcolor: "#374151", color: "#e5e7eb" }}
          />
          <Chip
            size="small"
            label={`${values.payServices.length} services`}
            sx={{ bgcolor: "#374151", color: "#e5e7eb" }}
          />
          <Chip
            size="small"
            label={
              REGION_META.find((r) => r.id === values.environmentRegion)
                ?.short ?? "—"
            }
            sx={{ bgcolor: "#374151", color: "#e5e7eb" }}
          />
        </Stack>
        {values.customerName ? (
          <Typography
            variant="body2"
            sx={{ color: "#d1d5db" }}
            noWrap
            title={values.customerName}
          >
            Customer: <strong>{values.customerName}</strong>
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Customer name appears here as you type.
          </Typography>
        )}
      </Stack>
    </>
  );

  const renderSidebar = (showClose: boolean) => (
    <Box className="dashboard-sidebar">
      {showClose ? (
        <Box className="dashboard-sidebar-close-row">
          <IconButton
            onClick={() => setMobileOpen(false)}
            className="dashboard-sidebar-close-icon"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      ) : null}
      {sidebarInner}
    </Box>
  );

  return (
    <Stack direction="row" className="dashboard-root">
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        className="dashboard-drawer"
        ModalProps={{ keepMounted: true }}
      >
        {renderSidebar(true)}
      </Drawer>

      <Stack direction="column" className="dashboard-sidebar-desktop">
        {renderSidebar(false)}
      </Stack>

      <Box className="dashboard-main" component="form" onSubmit={handleSubmit}>
        <Box className="dashboard-header-area">
          <Box className="dashboard-mobile-header">
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(true)}
              className="dashboard-mobile-header-icon"
              aria-label="open progress panel"
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="subtitle1"
              noWrap
              className="dashboard-mobile-header-title"
            >
              Pay onboarding
            </Typography>
            <Box className="dashboard-mobile-header-spacer" />
          </Box>
          <Box className="dashboard-page-title-desktop">
            <Typography variant="h4" className="dashboard-page-title">
              Agilysys Pay Onboarding
            </Typography>
          </Box>
          <Typography
            variant="body2"
            className="dashboard-page-subtitle-mobile"
            sx={{ px: 0.5 }}
          >
            Tap the menu to open progress and summary. Wide screens use two form
            columns.
          </Typography>
        </Box>

        <Box className="dashboard-content-area">
          <Box className="pay-onboarding-form-grid">
            <Stack spacing={2}>
              <SectionCard
                title="General"
                description="Customer and tenant context. Use business context to represent a property under a tenant or customer."
              >
                <Stack spacing={2}>
                  <TextField
                    label="Customer name"
                    value={values.customerName}
                    onChange={(e) => setField("customerName", e.target.value)}
                    fullWidth
                    required
                    autoComplete="organization"
                  />
                  <TextField
                    label="Platform tenant ID"
                    value={values.platformTenantId}
                    onChange={(e) =>
                      setField("platformTenantId", e.target.value)
                    }
                    fullWidth
                    helperText="Existing tenant ID, or leave context for a new tenant. Not required for another property under the same customer."
                  />
                </Stack>
              </SectionCard>

              <SectionCard
                title="Agilysys product"
                description="Tap chips to toggle. Selected items highlight in emerald."
              >
                <Box className="pay-onboarding-chip-wrap">
                  {AGILYSYS_PRODUCTS.map(({ id, label }) => (
                    <SelectableChip
                      key={id}
                      label={label}
                      selected={values.products.includes(id)}
                      onToggle={() =>
                        setField(
                          "products",
                          toggleInList(
                            values.products,
                            id,
                            !values.products.includes(id),
                          ),
                        )
                      }
                    />
                  ))}
                </Box>
              </SectionCard>

              <SectionCard
                title="Enterprise, division, and store"
                description="For InfoGenesis POS, IG Kiosk, IG OnDemand, and IG Fly."
              >
                <Stack spacing={2}>
                  <Stack spacing={2} direction={{ xs: "column", sm: "row" }}>
                    <TextField
                      label="Enterprise ID"
                      value={values.enterpriseId}
                      onChange={(e) => setField("enterpriseId", e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Enterprise name"
                      value={values.enterpriseName}
                      onChange={(e) =>
                        setField("enterpriseName", e.target.value)
                      }
                      fullWidth
                    />
                  </Stack>
                  <Stack spacing={2} direction={{ xs: "column", sm: "row" }}>
                    <TextField
                      label="Division ID"
                      value={values.divisionId}
                      onChange={(e) => setField("divisionId", e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Division name"
                      value={values.divisionName}
                      onChange={(e) => setField("divisionName", e.target.value)}
                      fullWidth
                    />
                  </Stack>
                  <Stack spacing={2} direction={{ xs: "column", sm: "row" }}>
                    <TextField
                      label="Store ID"
                      value={values.storeId}
                      onChange={(e) => setField("storeId", e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Store name"
                      value={values.storeName}
                      onChange={(e) => setField("storeName", e.target.value)}
                      fullWidth
                    />
                  </Stack>
                </Stack>
              </SectionCard>

              <SectionCard
                title="Profit center or outlet"
                description="One value per request — profit center for IG lines; outlet for Spa, Golf, Versa, Retail, SnC, Reserve."
              >
                <TextField
                  label="Profit center or outlet name"
                  value={values.profitCenterOrOutletName}
                  onChange={(e) =>
                    setField("profitCenterOrOutletName", e.target.value)
                  }
                  fullWidth
                />
              </SectionCard>
            </Stack>

            <Stack spacing={2}>
              <SectionCard
                title="Agilysys Pay service"
                description="Tap to combine multiple services."
              >
                <Box className="pay-onboarding-chip-wrap">
                  {PAY_SERVICES.map(({ id, label }) => (
                    <SelectableChip
                      key={id}
                      label={label}
                      selected={values.payServices.includes(id)}
                      onToggle={() =>
                        setField(
                          "payServices",
                          toggleInList(
                            values.payServices,
                            id,
                            !values.payServices.includes(id),
                          ),
                        )
                      }
                    />
                  ))}
                </Box>
              </SectionCard>

              <SectionCard
                title="Agilysys Pay wallets"
                description="Optional wallet methods."
              >
                <Box className="pay-onboarding-chip-wrap">
                  {PAY_WALLETS.map(({ id, label }) => (
                    <SelectableChip
                      key={id}
                      label={label}
                      selected={values.wallets.includes(id)}
                      onToggle={() =>
                        setField(
                          "wallets",
                          toggleInList(
                            values.wallets,
                            id,
                            !values.wallets.includes(id),
                          ),
                        )
                      }
                    />
                  ))}
                </Box>
              </SectionCard>

              <SectionCard title="PMS and gateway">
                <Stack spacing={2}>
                  <TextField
                    label="PMS destination URL"
                    value={values.pmsDestinationUrl}
                    onChange={(e) =>
                      setField("pmsDestinationUrl", e.target.value)
                    }
                    fullWidth
                    placeholder="e.g. pay.rguest.com"
                    helperText={
                      <>
                        PIP only. Sample:{" "}
                        <Link
                          href="https://pay.rguest.com/payment-proxy-service/v1/PaymentProxyService/?alias=<alias-name>"
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                        >
                          PaymentProxyService
                        </Link>
                      </>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.enableCaptcha}
                        onChange={(_, c) => setField("enableCaptcha", c)}
                        color="primary"
                      />
                    }
                    label="Enable captcha"
                  />
                  <FormHelperText sx={{ mt: -1 }}>
                    Recommended for security where supported.
                  </FormHelperText>
                  <TextField
                    label="Payment gateway"
                    value={values.paymentGateway}
                    onChange={(e) => setField("paymentGateway", e.target.value)}
                    fullWidth
                    helperText="For PIP and iFrame."
                  />
                </Stack>
              </SectionCard>

              <SectionCard
                title="Environment and region"
                description="Pick a region card — details update below."
              >
                <Box className="pay-onboarding-region-row" sx={{ mb: 2 }}>
                  {REGION_META.map(({ id, title, short }) => {
                    const active = values.environmentRegion === id;
                    return (
                      <Box
                        key={id}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(ev) => {
                          if (ev.key === "Enter" || ev.key === " ") {
                            ev.preventDefault();
                            setField("environmentRegion", id);
                          }
                        }}
                        onClick={() => setField("environmentRegion", id)}
                        className={
                          active
                            ? "pay-onboarding-region-card pay-onboarding-region-card--active"
                            : "pay-onboarding-region-card"
                        }
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {short}
                            </Typography>
                          </Box>
                          {active ? (
                            <CheckCircleOutline
                              sx={{ color: "primary.main", fontSize: 22 }}
                            />
                          ) : null}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  {environmentHelp[values.environmentRegion]}
                </Alert>
              </SectionCard>
            </Stack>
          </Box>
        </Box>

        <Box className="dashboard-footer">
          <Stack
            className="dashboard-footer-row"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="caption" color="text.secondary">
              Agilysys Inc
            </Typography>
            <Stack
              className="form-checkbox-row form-two-col-row"
              direction="row"
              spacing={1.5}
              flexWrap="wrap"
              useFlexGap
            >
              <Button
                type="button"
                variant="outlined"
                color="inherit"
                onClick={handleReset}
              >
                Clear form
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="medium"
              >
                Review &amp; save locally
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity="info"
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

const AgilysysPayOnboardingForm: React.FC = () => (
  <ThemeProvider theme={dashboardAppTheme}>
    <CssBaseline />
    <AgilysysPayOnboardingFormInner />
  </ThemeProvider>
);

export default AgilysysPayOnboardingForm;
